import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  FlexColumn,
  FlexRow,
  HashLength,
  SvgIcon,
} from '@make-software/cspr-ui';

import {
  ACCOUNT_DETAILS_PATH,
  VALIDATOR_DETAILS_PATH,
} from '../../router/paths';
import { buildRouterPath } from '../../router/utils';
import { isValidPublicKey } from '../../utils/account';
import {
  AccountInfoAccordion,
  AccountInfoTable,
} from '../account-info-accordion/account-info-accordion';
import DetailsTableRow from '../details-table-row/details-table-row';
import DetailsNameTableData from '../details-name-table-data/details-name-table-data';
import DetailsValueTableData from '../details-value-table-data/details-value-table-data';
import { HashLink } from '../hash-link/hash-link';
import { DetailsLocation } from '../details-location/details-location';

const OwnerNodeHeader = styled('div')(({ theme }) =>
  theme.withMedia({
    padding: '12px 20px 12px 24px',
    lineHeight: '20px',
  })
);
const OwnerNodeCell = styled('div')(({ theme }) =>
  theme.withMedia({
    padding: '12px 20px 12px 0px',
    lineHeight: '20px',
  })
);

export const DetailsAccordionNodes = ({
  accordionTitle,
  otherNodes,
  isAccountVerified,
}: {
  accordionTitle: React.ReactElement;
  otherNodes;
  isAccountVerified;
}) => {
  const { t } = useTranslation();

  const getValidatorPagePath = (node, publicKey) => {
    const path = node.functionality?.indexOf('validator')
      ? ACCOUNT_DETAILS_PATH
      : VALIDATOR_DETAILS_PATH;

    return buildRouterPath(path, { id: publicKey }, undefined);
  };

  return (
    (otherNodes?.filter((i) => isValidPublicKey(i.public_key)).length ||
      null) && (
      <AccountInfoAccordion
        disableClickAway
        renderContent={() => (
          <AccountInfoTable
            renderData={() =>
              otherNodes
                ?.filter((i) => isValidPublicKey(i.public_key))
                .map((node, index) => {
                  const publicKey: string = node.public_key!;
                  const href = getValidatorPagePath(node, publicKey);

                  return (
                    <>
                      <DetailsTableRow key={node.public_key + '0'}>
                        <DetailsNameTableData>
                          <Trans t={t}>Node</Trans> {index + 1}
                        </DetailsNameTableData>
                        <DetailsValueTableData monotype>
                          <FlexRow align={'center'}>
                            <HashLink
                              href={href}
                              hashLength={HashLength.FULL}
                              hash={publicKey}
                            />
                            {isAccountVerified(publicKey) && (
                              <SvgIcon
                                src="assets/icons/ic-verified.svg"
                                marginLeft
                              />
                            )}
                          </FlexRow>
                        </DetailsValueTableData>
                      </DetailsTableRow>
                      <DetailsTableRow key={node.public_key + '1'}>
                        <DetailsNameTableData verticalAlign={'baseline'}>
                          <FlexColumn>
                            {(node.functionality?.length || null) && (
                              <OwnerNodeHeader>
                                <Trans t={t}>Functionality</Trans>
                              </OwnerNodeHeader>
                            )}
                            {node.location?.country && (
                              <OwnerNodeHeader>
                                <Trans t={t}>Location</Trans>
                              </OwnerNodeHeader>
                            )}
                            {node.description && (
                              <OwnerNodeHeader>
                                <Trans t={t}>Description</Trans>
                              </OwnerNodeHeader>
                            )}
                          </FlexColumn>
                        </DetailsNameTableData>
                        <DetailsValueTableData>
                          <FlexColumn>
                            {(node.functionality?.length || null) && (
                              <OwnerNodeCell
                                style={{ textTransform: 'capitalize' }}
                              >
                                {node.functionality?.join(', ')}
                              </OwnerNodeCell>
                            )}
                            {node.location?.country && (
                              <OwnerNodeCell>
                                <DetailsLocation location={node?.location} />
                              </OwnerNodeCell>
                            )}
                            {node.description && (
                              <OwnerNodeCell>{node.description}</OwnerNodeCell>
                            )}
                          </FlexColumn>
                        </DetailsValueTableData>
                      </DetailsTableRow>
                    </>
                  );
                })
            }
          />
        )}
      >
        {accordionTitle}
      </AccountInfoAccordion>
    )
  );
};
